import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { HiArrowTopRightOnSquare, HiOutlineChevronDown } from "react-icons/hi2";
import toast from "react-hot-toast";

import SelectMonth from "../../components/selectMonth";
import SelectContact from "../../components/SelectContact";

import api from "../../services/api";

const Activity = ({ businessUnit }) => {
  const [date, setDate] = useState(null);
  const [contract, setContarct] = useState("");
  const [managers, setManagers] = useState([]);
  const [managerId, setManagerId] = useState("");
  const [users, setUsers] = useState([]);

  // "ALL" is the "All Teams" sentinel from the sidebar selector — no filtering then.
  const isScoped = businessUnit?._id && businessUnit._id !== "ALL";

  const getManagers = async () => {
    try {
      const query = {
        role: ["super_admin", "admin", "user"],
        contracts: ["VIE", "CDD", "CDI", "APPRENTICESHIP", "INTERNSHIP", "FREELANCE", "Me kohe te plote"],
        status: "active",
      };
      if (isScoped) query.business_unit_id = businessUnit._id;
      const { ok, data } = await api.post(`/user/search`, query);
      const _managers = data.users.filter((u) => data.users.some((otherUser) => otherUser.manager_id === u._id));
      setManagers(_managers);
    } catch (error) {
      console.error(error);
      toast.error("Error while fetching managers");
    }
  };

  const getUsers = async () => {
    try {
      const query = {
        role: ["super_admin", "admin", "user"],
        contracts: ["VIE", "CDD", "CDI", "APPRENTICESHIP", "INTERNSHIP", "FREELANCE", "Me kohe te plote"],
        status: "active",
      };
      if (isScoped) query.business_unit_id = businessUnit._id;
      if (managerId) query.manager_id = managerId;
      const manager = managers.find((m) => m._id === managerId);
      const { ok, data } = await api.post(`/user/search`, query);
      if (manager) setUsers([manager, ...data.users]);
      else setUsers(data.users);
    } catch (error) {
      console.error(error);
      toast.error("Error while fetching users");
    }
  };

  useEffect(() => {
    getManagers();
  }, [businessUnit?._id]);

  useEffect(() => {
    getUsers();
  }, [managerId, businessUnit?._id]);

  useEffect(() => {
    const search = window.location.search;
    const params = new URLSearchParams(search);
    const date = params.get("date");
    if (date) setDate(new Date(date));
  }, []);

  return (
    <div className="overflow-hidden w-screen md:w-full space-y-4">
      <div className="flex items-center gap-3 bg-white rounded-xl px-5 py-4 shadow-sm">
        <SelectMonth start={-11} indexDefaultValue={11} value={date} onChange={(e) => setDate(e.target.value)} showArrows />
        <SelectContact value={contract} onChange={(contract) => setContarct(contract)} />
        <select
          className="w-[180px] bg-gray-50 text-sm font-normal py-2 px-3 rounded-lg border border-gray-200 cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 transition-colors"
          value={managerId}
          onChange={(e) => setManagerId(e.target.value)}>
          <option value={""}>All managers</option>
          {managers.map((manager) => (
            <option value={manager._id}>{manager.name}</option>
          ))}
        </select>
      </div>

      {users.map((user) => (
        <Activities key={user._id} date={date} user={user} />
      ))}
    </div>
  );
};

const Field = ({ value = 0 }) => {
  let badgeBg = "";
  let badgeText = "";

  if (value >= 7) {
    badgeBg = "bg-[#2d6a4f]";
    badgeText = "text-white";
  } else if (value >= 5) {
    badgeBg = "bg-[#40916c]";
    badgeText = "text-white";
  } else if (value >= 3) {
    badgeBg = "bg-[#52b788]";
    badgeText = "text-white";
  } else if (value > 0) {
    badgeBg = "bg-[#95d5b2]";
    badgeText = "text-[#1b4332]";
  }

  if (value === 0 || !value) {
    return (
      <td className="py-1 px-0 text-center w-[36px]">
        <span className="text-[12px] text-gray-300 tabular-nums">0</span>
      </td>
    );
  }

  return (
    <td className="py-1 px-[2px] text-center w-[36px]">
      <div className={`w-[30px] h-[28px] flex items-center justify-center ${badgeBg} ${badgeText} rounded-md text-[12px] font-bold tabular-nums mx-auto`}>
        {value}
      </div>
    </td>
  );
};

const Activities = ({ date, user }) => {
  const [activities, setActivities] = useState([]);
  const [open, setOpen] = useState(true);

  useEffect(() => {
    get();
  }, [date, user]);

  const navigate = useNavigate();

  async function get() {
    try {
      const { data } = await api.post(`/activity/search`, { date: date.getTime(), userId: user._id });
      setActivities(data);
    } catch (e) {
      console.error(e);
    }
  }

  const days = getDaysInMonth(date.getMonth(), date.getFullYear());

  const getWorkingDays = () => {
    return days.reduce((acc, a) => {
      const day = a.getDay();
      if (day === 0 || day == 6) return acc;
      return acc + 1;
    }, 0);
  };

  const getTotal = () => (activities.reduce((acc, a) => acc + a.total, 0) / 8).toFixed(2);

  return (
    <div className="bg-white rounded-xl shadow-sm overflow-hidden">
      <div className="w-screen md:w-full">
        <div className="flex items-center justify-between px-4 py-3 cursor-pointer hover:bg-[#faf8f5]/60 transition-colors" onClick={() => setOpen(!open)}>
          <div className="flex items-center gap-3">
            <img src={user.avatar} alt={user.name} className="w-9 h-9 rounded-full object-cover ring-2 ring-gray-100" />
            <span className="font-bold text-sm text-gray-900">{user.name}</span>
          </div>
          <Stats activities={activities} />
          <HiOutlineChevronDown className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
        </div>
        {open && (
          <div className="border-t border-gray-100 overflow-auto">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="py-2 text-[11px] font-semibold text-gray-400 text-left pl-4 uppercase tracking-wider">Budgets</th>
                    {days.map((e) => {
                      const days = ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"];
                      const _date = new Date(e);
                      const day = _date.getDay();
                      const weekday = days[day];
                      const date = _date.getDate();
                      const isWeekend = day == 0 || day == 6;
                      return (
                        <th className="w-[36px] text-center py-1.5" key={e} day={day}>
                          <div className={`text-[10px] font-medium ${isWeekend ? "text-rose-400" : "text-gray-400"} uppercase`}>{weekday}</div>
                          <div className={`text-[13px] font-bold ${isWeekend ? "text-rose-500" : "text-gray-700"}`}>{date}</div>
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-gray-200">
                    <th className="px-4 py-2">
                      <div className="flex justify-end w-full text-[13px] font-bold text-gray-800">
                        {`${getTotal()} / ${getWorkingDays()} days`}
                      </div>
                    </th>
                    {days.map((e, i) => {
                      const v = activities.reduce((acc, a) => {
                        if (!a.detail[i]) return acc;
                        return acc + a.detail[i].value;
                      }, 0);
                      return <Field key={`day-${i}`} value={v} disabled />;
                    })}
                  </tr>
                  {activities.map((e, i) => {
                    return (
                      <React.Fragment key={`1-${e._id}`}>
                        <tr className="border-b border-gray-100 hover:bg-[#faf8f5]/60 transition-colors" key={`1-${e._id}`}>
                          <th className="w-[100px] text-[12px] text-gray-800 text-left">
                            <div className="flex flex-1 items-center justify-between gap-2 px-4 py-1.5">
                              <div className="flex flex-col flex-1 items-start cursor-pointer hover:text-blue-600 transition-colors" onClick={() => navigate(`/project/${e.projectId}`)}>
                                <div className="font-bold text-[13px]">{e.projectName}</div>
                                <div className="text-[11px] text-gray-400 font-normal">{e.budgetName}</div>
                              </div>
                              <div className="flex flex-col items-end">
                                <div className="text-[11px] text-gray-600 font-medium tabular-nums">{(e.total / 8).toFixed(1)}d</div>
                                <div className="text-[10px] text-gray-400 font-normal tabular-nums">{(((e.total / 8).toFixed(2) / getTotal()) * 100).toFixed(1)}%</div>
                              </div>
                            </div>
                          </th>
                          {e.detail.map((f, j) => {
                            return (
                              <Field
                                key={`${e.projectName} ${j}`}
                                value={f.value || 0}
                              />
                            );
                          })}
                        </tr>
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const budgetTypeColors = {
  project: "bg-emerald-100 text-emerald-800 border border-emerald-200",
  prediction: "bg-purple-100 text-purple-800 border border-purple-200",
  admin: "bg-amber-100 text-amber-800 border border-amber-200",
  invest: "bg-orange-100 text-orange-800 border border-orange-200",
  overbudget: "bg-red-100 text-red-800 border border-red-200",
};

const Stats = ({ activities }) => {
  const total = activities.reduce((acc, activity) => acc + activity.total / 8, 0);

  const byBudgetType = activities.reduce((acc, activity) => {
    const hours = activity.total / 8;
    return {
      ...acc,
      [activity.budgetType]: (acc[activity.budgetType] || 0) + hours,
    };
  }, {});

  const budgetStats = Object.entries(byBudgetType)
    .map(([budgetType, hours]) => ({
      budgetType,
      hours,
      percentage: ((hours / total) * 100).toFixed(0),
    }))
    .sort((a, b) => b.hours - a.hours);

  return (
    <div className="flex items-center gap-1.5">
      {budgetStats.map(({ budgetType, percentage }) => (
        <span key={budgetType} className={`text-[11px] font-medium px-2 py-0.5 rounded-full whitespace-nowrap ${budgetTypeColors[budgetType] || "bg-gray-50 text-gray-600"}`}>
          {budgetType} {percentage}%
        </span>
      ))}
    </div>
  );
};

export default Activity;

function getDaysInMonth(month, year) {
  var date = new Date(year, month, 1);
  date.setHours(0, 0, 0, 0);
  var days = [];
  while (date.getMonth() === month) {
    days.push(new Date(date));
    date.setDate(date.getDate() + 1);
  }
  return days;
}
